import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ProgressTrackerLinks.css";

interface Link {
  id: string;
  title: string;
  description: string;
  url: string;
  category: string;
  icon: string;
}

interface ProgressTrackerLinksProps {
  applicationId: string;
  status: "interview_scheduled" | "accepted" | "rejected";
  jobTitle: string;
  company: string;
  onClose: () => void;
  isPremium: boolean;
  updatedVia?: "manual" | "email-sync"; // Track how the status was updated
}

const ProgressTrackerLinks: React.FC<ProgressTrackerLinksProps> = ({
  applicationId,
  status,
  jobTitle,
  company,
  onClose,
  isPremium,
  updatedVia
}) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissedLinks, setDismissedLinks] = useState<Set<string>>(new Set());

  // Load contextual links based on status
  useEffect(() => {
    const loadLinks = async () => {
      try {
        // Mock links - in production, backend would generate these
        let contextLinks: Link[] = [];

        if (status === "interview_scheduled") {
          contextLinks = [
            {
              id: "interview-prep-1",
              title: "Interview Preparation Guide",
              description: "Prepare for your interview at " + company + " using Sumbitrbuddy Interview Prep",
              url: "/interview-prep",
              category: "preparation",
              icon: "📝"
            },
            {
              id: "interview-prep-2",
              title: "Mock Interview Questions",
              description: "Practice common interview questions for the " + jobTitle + " role",
              url: "/interview-prep",
              category: "practice",
              icon: "🎤"
            },
            {
              id: "company-research",
              title: "Research " + company,
              description: "Visit the company's website to learn about their culture and products",
              url: "https://www.google.com/search?q=" + encodeURIComponent(company + " official website"),
              category: "research",
              icon: "🔍"
            },
            {
              id: "salary-info",
              title: "Salary Benchmark",
              description: "Review typical salary ranges for " + jobTitle + " roles in Analytics",
              url: "/analytics",
              category: "salary",
              icon: "💰"
            }
          ];
        } else if (status === "accepted") {
          contextLinks = [
            {
              id: "onboarding-1",
              title: "Onboarding Checklist",
              description: "Prepare everything you need before your first day at " + company,
              url: "#",
              category: "onboarding",
              icon: "🚀"
            },
            {
              id: "offer-eval",
              title: "Offer Evaluation Guide",
              description: "Review your offer, benefits, and contract terms carefully",
              url: "#",
              category: "offer",
              icon: "📋"
            },
            {
              id: "date-prep",
              title: "Prepare for Start Date",
              description: "Set up your workspace, confirm logistics, and get ready",
              url: "#",
              category: "preparation",
              icon: "📅"
            },
            {
              id: "celebrate",
              title: "🎉 You Got the Job!",
              description: "Celebrate your win - you earned it!",
              url: "#",
              category: "celebration",
              icon: "⭐"
            }
          ];
        } else if (status === "rejected") {
          contextLinks = [
            {
              id: "similar-jobs",
              title: "Similar Job Opportunities",
              description: "Search for similar " + jobTitle + " roles",
              url: "/job-search?q=" + encodeURIComponent(jobTitle),
              category: "opportunities",
              icon: "📌"
            },
            {
              id: "reapply-tips",
              title: "Reapplication Strategy",
              description: "Reflect on your application and plan your next move",
              url: "#",
              category: "strategy",
              icon: "🔄"
            },
            {
              id: "skill-improvement",
              title: "Improve Your Skills",
              description: "Explore career development resources and IT career switch pathways",
              url: "https://futuresecure.ca/career-switch",
              category: "learning",
              icon: "📚"
            },
            {
              id: "interview-feedback",
              title: "Request Interview Feedback",
              description: "Draft an email to the employer requesting feedback on your application",
              url: "mailto:?subject=" + encodeURIComponent("Interview Feedback Request — " + jobTitle + " at " + company) + "&body=" + encodeURIComponent("Dear Hiring Manager,\n\nThank you for considering my application for the " + jobTitle + " role at " + company + ". While I was disappointed not to be selected, I would greatly appreciate any feedback you could share to help me improve.\n\nThank you for your time.\n\nBest regards,"),
              category: "feedback",
              icon: "💡"
            }
          ];
        }

        setLinks(contextLinks);
      } catch (err) {
        console.error("Failed to load contextual links:", err);
      } finally {
        setLoading(false);
      }
    };

    loadLinks();

    // Load dismissed links from localStorage
    const key = `dismissed-links-${applicationId}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      setDismissedLinks(new Set(JSON.parse(saved)));
    }
  }, [applicationId, status, company, jobTitle]);

  const handleDismissLink = (linkId: string) => {
    const newDismissed = new Set(dismissedLinks);
    newDismissed.add(linkId);
    setDismissedLinks(newDismissed);

    // Persist to localStorage
    const key = `dismissed-links-${applicationId}`;
    localStorage.setItem(key, JSON.stringify(Array.from(newDismissed)));

    // If all links are dismissed, close the tracker
    const remainingLinks = links.filter(link => !newDismissed.has(link.id));
    if (remainingLinks.length === 0) {
      onClose();
    }
  };

  const handleDismissAll = () => {
    const allLinkIds = links.map(link => link.id);
    const key = `dismissed-links-${applicationId}`;
    localStorage.setItem(key, JSON.stringify(allLinkIds));
    onClose();
  };

  const visibleLinks = links.filter(link => !dismissedLinks.has(link.id));

  if (loading) {
    return (
      <div className="progress-tracker-container">
        <div className="progress-tracker-loading">Loading suggestions...</div>
      </div>
    );
  }

  if (visibleLinks.length === 0) {
    return null;
  }

  const getTitleForStatus = () => {
    const prefix = updatedVia === "email-sync" ? "📧 Auto-Updated: " : "🎯 ";
    switch (status) {
      case "interview_scheduled":
        return `${prefix}Interview Coming Up!`;
      case "accepted":
        return `${prefix}Congratulations! You Got It!`;
      case "rejected":
        return `${prefix}Keep Moving Forward`;
      default:
        return "Helpful Resources";
    }
  };

  return (
    <div className="progress-tracker-container">
      <div className="progress-tracker-modal">
        <div className="progress-tracker-header">
          <h2>{getTitleForStatus()}</h2>
          <button
            className="progress-tracker-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="progress-tracker-subtitle">
          {updatedVia === "email-sync" && (
            <span className="update-method-badge">📧 Status auto-updated from email • </span>
          )}
          {status === "interview_scheduled" && "Prepare for your upcoming interview at " + company}
          {status === "accepted" && "You're ready for the next chapter - here's how to prepare"}
          {status === "rejected" && "Use these resources to keep moving forward in your job search"}
        </div>

        <div className="progress-tracker-links">
          {visibleLinks.map((link) => (
            <div key={link.id} className="link-item">
              <div className="link-content">
                <div className="link-header">
                  <span className="link-icon">{link.icon}</span>
                  <h3>{link.title}</h3>
                </div>
                <p className="link-description">{link.description}</p>
              </div>
              <div className="link-actions">
                {link.url !== "#" && (
                  link.url.startsWith("/") ? (
                    <button className="link-button" onClick={() => { onClose(); navigate(link.url); }}>
                      View
                    </button>
                  ) : (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-button">
                      View
                    </a>
                  )
                )}
                <button
                  className="link-dismiss"
                  onClick={() => handleDismissLink(link.id)}
                  aria-label="Dismiss this link"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {!isPremium && (
          <div className="premium-prompt">
            <span>✨</span>
            <p>Premium users get AI-generated suggestions tailored to their profile!</p>
          </div>
        )}

        <div className="progress-tracker-footer">
          <button
            className="progress-tracker-dismiss-all"
            onClick={handleDismissAll}
          >
            Dismiss All
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressTrackerLinks;
